import apiClient from './api';

// ==================== 认证相关API ====================

/**
 * 密码登录
 */
export const passwordLogin = async (username: string, password: string) => {
  return await apiClient.post('/auth/password-login', {
    username,
    password,
  });
};

/**
 * 手机号验证码登录
 */
export const phoneLogin = async (phone: string, smsCode: string) => {
  return await apiClient.post('/auth/phone-login', {
    phone,
    smsCode,
  });
};

/**
 * 发送短信验证码
 */
export const sendSmsCode = async (phone: string) => {
  return await apiClient.post('/auth/send-sms', { phone });
};

/**
 * 刷新Token
 */
export const refreshToken = async (refreshToken: string) => {
  return await apiClient.post('/auth/refresh-token', { refreshToken });
};

/**
 * 登出
 */
export const logout = async () => {
  return await apiClient.post('/auth/logout');
};

// ==================== 仪表盘统计API ====================

/**
 * 获取仪表盘统计数据（管理员）
 */
export const getDashboardStats = async () => {
  return await apiClient.get('/admin/dashboard-stats');
};

/**
 * 获取用户个人数据
 */
export const getUserProfile = async () => {
  return await apiClient.get('/users/profile');
};

/**
 * 获取推广统计数据（推荐官）
 */
export const getPromotionStats = async () => {
  return await apiClient.get('/promotion/stats');
};

/**
 * 业绩计算（管理员）
 */
export const calculatePerformance = async (data: {
  role: string;
  monthlyUsage: number;
  retailPrice: number;
  purchaseCost: number;
  directUsers?: number;
  calculationPeriod?: string;
  platformFee?: number;
}) => {
  return await apiClient.post('/admin/calculate-performance', data);
};

// ==================== 管理员管理API（仅超级管理员） ====================

/**
 * 创建管理员账号
 */
export const createAdmin = async (data: {
  phone: string;
  password: string;
  nickname?: string;
  isSuperAdmin: boolean;
}) => {
  return await apiClient.post('/admin/admins', data);
};

/**
 * 获取管理员列表
 */
export const getAdminList = async (params?: {
  page?: number;
  pageSize?: number;
  keyword?: string;
  isSuperAdmin?: boolean;
}) => {
  return await apiClient.get('/admin/admins', { params });
};

/**
 * 更新管理员信息
 */
export const updateAdmin = async (
  id: number,
  data: {
    nickname?: string;
    status?: 'active' | 'inactive' | 'suspended';
  }
) => {
  return await apiClient.put(`/admin/admins/${id}`, data);
};

/**
 * 删除管理员账号
 */
export const deleteAdmin = async (id: number) => {
  return await apiClient.delete(`/admin/admins/${id}`);
};

// ==================== 用户管理API（管理员可查看） ====================

/**
 * 获取用户列表
 */
export const getUserList = async (params?: {
  page?: number;
  pageSize?: number;
  keyword?: string;
  role?: 'retail' | 'level1' | 'level2' | 'level3' | 'regional' | 'admin';
  status?: 'active' | 'inactive' | 'suspended';
}) => {
  return await apiClient.get('/admin/users', { params });
};

/**
 * 删除用户（管理员或超级管理员）
 */
export const deleteUser = async (id: number) => {
  return await apiClient.delete(`/admin/users/${id}`);
};

// ==================== 推荐官管理API（管理员可查看） ====================

/**
 * 获取推荐官层级关系（树形结构）
 */
export const getReferralTree = async (userId?: number) => {
  return await apiClient.get('/admin/referral-tree', {
    params: userId ? { userId } : undefined,
  });
};

/**
 * 获取推荐官收益情况
 */
export const getReferralEarnings = async (params?: {
  page?: number;
  pageSize?: number;
  userId?: number;
  periodStart?: string;
  periodEnd?: string;
}) => {
  return await apiClient.get('/admin/referral-earnings', { params });
};

// ==================== 系统配置API ====================

/**
 * 修改系统配置（仅超级管理员）
 */
export const updateSystemConfig = async (
  configKey: string,
  configValue: string,
  description?: string
) => {
  return await apiClient.put('/admin/system-config', {
    configKey,
    configValue,
    description,
  });
};

/**
 * 获取系统配置
 */
export const getSystemConfig = async (key?: string) => {
  return await apiClient.get('/admin/system-config', {
    params: key ? { key } : undefined,
  });
};

/**
 * 修改APP版本号（仅超级管理员）
 */
export const updateAppVersion = async (data: {
  version: string;
  appName?: string;
  apiVersion?: string;
}) => {
  return await apiClient.put('/admin/app-version', data);
};

/**
 * 获取APP版本号
 */
export const getAppVersion = async () => {
  return await apiClient.get('/admin/app-version');
};

// ==================== 操作日志API（仅超级管理员） ====================

/**
 * 获取操作日志
 */
export const getOperationLogs = async (params?: {
  page?: number;
  pageSize?: number;
  adminId?: number;
  action?: string;
  module?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return await apiClient.get('/admin/operation-logs', { params });
};

// ==================== 分润配置API ====================

/**
 * 获取分润配置（公开接口）
 */
export const getProfitConfig = async () => {
  return await apiClient.get('/profit-config');
};

/**
 * 更新单个分润配置（仅管理员）
 */
export const updateProfitConfig = async (
  configKey: string,
  configValue: number,
  description?: string
) => {
  return await apiClient.put(`/profit-config/${configKey}`, {
    configValue,
    description,
  });
};

/**
 * 批量更新分润配置（仅管理员）
 */
export const batchUpdateProfitConfig = async (configs: {
  PLATFORM_FEE_RATE?: number;
  DIRECT_REFERRAL_RATE?: number;
  TEAM_MANAGEMENT_RATE?: number;
  TEAM_REWARD_RATE?: number;
}) => {
  return await apiClient.put('/profit-config', configs);
};

// ==================== 系统版本API（公开接口） ====================

/**
 * 获取系统版本信息
 */
export const getSystemVersion = async () => {
  return await apiClient.get('/system/version');
};

export default {
  // 认证
  passwordLogin,
  phoneLogin,
  sendSmsCode,
  refreshToken,
  logout,
  
  // 管理员管理
  createAdmin,
  getAdminList,
  updateAdmin,
  deleteAdmin,
  
  // 用户管理
  getUserList,
  
  // 推荐官管理
  getReferralTree,
  getReferralEarnings,
  
  // 系统配置
  updateSystemConfig,
  getSystemConfig,
  updateAppVersion,
  getAppVersion,
  
  // 操作日志
  getOperationLogs,
  
  // 分润配置
  getProfitConfig,
  updateProfitConfig,
  batchUpdateProfitConfig,
  
  // 系统版本
  getSystemVersion,
};